import React, {
	memo,
	UIEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { ProfileModal } from '@/components/ProfileModal';
import { UserAvatar } from '@/components/UserAvatar';
import {
	ASAPH_BOT_DISPLAY_NAME,
	ASAPH_BOT_USER_ID,
	sendingMessageStatus,
} from '@/config/constants';
import { ChatType, UserChatStat } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { debounce } from '@/helpers/debounce';
import { useAuth } from '@/providers/AuthProvider';
import { useTeam } from '@/providers/TeamProvider';
import { Capacitor } from '@capacitor/core';
import { CircularProgress } from '@material-ui/core';
import {
	differenceInMinutes,
	format,
	isSameDay,
	isSameWeek,
	isSameYear,
	subDays,
} from 'date-fns';

import { ChatContainerMessage, IChatContainerProps } from '../../ChatContainer';
import styles from './ChatList.module.scss';
import { ChatHistoryGate } from './components/ChatHistoryGate';
import { MessageHeader } from './components/MessageHeader';
import { RenderMessage, RenderMessageProps } from './components/RenderMessage';

const RenderMessageMemoized = memo(RenderMessage);

export interface IChatListProps {
	chat: IChatContainerProps['chat'];
	userStats?: UserChatStat[] | null;
	fetchPreviousMessages: () => Promise<any>;
	padding?: 'normal' | 'increased';
	previousMessagesLoading: boolean;
	hasMoreMessages: boolean;
	focusedMessageId?: string;
	setFocusedMessageId: React.Dispatch<
		React.SetStateAction<string | undefined>
	>;
	isGated?: boolean;
	isAdmin?: boolean;
}

const isCapacitor = Capacitor.isNativePlatform();
// const isCapacitor = true;

export const ChatList: React.FC<IChatListProps> = ({
	chat,
	userStats,
	fetchPreviousMessages,
	padding,
	hasMoreMessages,
	previousMessagesLoading,
	focusedMessageId,
	setFocusedMessageId,
	isGated,
	isAdmin,
}) => {
	const { user } = useAuth();
	const { findTeamMembersByUserId } = useTeam();
	const chatListRef = useRef<HTMLDivElement>(null);
	const chatListMeasurementRef = useRef<HTMLDivElement>(null);

	const [scrolledToBottom, setScrolledToBottom] = useState<boolean>(true);
	const [lastSeenMessageTimestamp, setLastSeenMessageTimestamp] =
		useState<string>(new Date(0).toISOString());
	const [newMessage, setNewMessage] = useState<
		| NonNullable<
				NonNullable<IChatContainerProps['chat']>['messages']
		  >['items'][0]
		| undefined
	>();
	const [containerHeight, setContainerHeight] = useState<number>();
	const [openMessageUserStats, setOpenMessageUserStats] = useState<
		string | null
	>(null);
	const [selectedProfileMemberId, setSelectedProfileMemberId] = useState<
		string | null
	>(null);
	const lastSelectedProfileMemberIdRef = useRef<string | null>(null);
	if (selectedProfileMemberId !== null)
		lastSelectedProfileMemberIdRef.current = selectedProfileMemberId;

	useEffect(() => {
		// We need to wrap this call in a timeout as we are hiding the
		// bottom navigation bar at the same time we do the initial render.
		// Therefore the measurement does not include the removed height of
		// the navigation panel. Check for this code snippet in `@/pages/Chat/ChatPage.tsx`
		/*
    	useEffect(() => {
        setHideBottomMenu(true);
        return () => setHideBottomMenu(false);
      }, [setHideBottomMenu]);
    */
		// We also need to re-run this useEffect whenever there is a change
		// in the overall height of the chat container. At the time of writing
		// this is when a setlist (draft or not) is added/deleted.
		setTimeout(() => {
			if (!chatListMeasurementRef.current || !isCapacitor) {
				return;
			}
			const measurementDivHeight =
				chatListMeasurementRef.current!.clientHeight;
			setContainerHeight(measurementDivHeight);
		}, 500);
	}, []);

	// useMemo runs before the code renders
	// it's here used as an equivalent of getSnapshotBeforeUpdate
	useMemo(() => {
		if (!chatListRef.current) {
			return;
		}
		const msgContainer = chatListRef.current;
		const scrollBottom =
			msgContainer.scrollHeight -
			msgContainer.clientHeight -
			msgContainer.scrollTop;

		setTimeout(() => {
			if (chatListRef.current) {
				const msgContainer = chatListRef.current;
				const scrollTop =
					msgContainer.scrollHeight -
					msgContainer.clientHeight -
					scrollBottom;
				msgContainer.scrollTo({
					top: scrollTop,
				});
			}
		}, 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat?.messages?.items.length]);

	const type = useMemo(() => {
		if (!chat) {
			return 'private';
		}
		return chat.type === ChatType.Group || chat.type === ChatType.Video
			? 'group'
			: 'private';
	}, [chat]);

	useEffect(() => {
		if (!chatListRef.current) {
			return;
		}

		const messages = chat?.messages?.items || [];
		const lastMessage = messages.at(-1);
		const isSending = lastMessage?.createdAt === sendingMessageStatus;

		const lastMessageTimestamp = lastMessage?.createdAt || '';
		setLastSeenMessageTimestamp(lastMessageTimestamp);

		if (scrolledToBottom || isSending) {
			setTimeout(() => {
				if (chatListRef.current) {
					chatListRef.current.scrollTo({
						top: chatListRef.current.scrollHeight,
					});
				}
			}, 2);
		} else if (
			new Date(lastSeenMessageTimestamp || 0).getTime() <
			new Date(lastMessageTimestamp || 0).getTime()
		) {
			setNewMessage(lastMessage);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat?.messages?.items.length]);

	const handleNewMessageClick = useCallback(() => {
		if (!chatListRef.current) {
			return;
		}
		chatListRef.current.scrollTo({
			top: chatListRef.current.scrollHeight,
			behavior: 'smooth',
		});
	}, []);

	const onScroll = (e: UIEvent<HTMLDivElement>) => {
		const scrollContainer = e.target as HTMLDivElement;
		const isScrolledToBottom =
			scrollContainer.scrollHeight -
				scrollContainer.scrollTop -
				scrollContainer.clientHeight <
			100;
		setScrolledToBottom(isScrolledToBottom);

		if (isScrolledToBottom) {
			setNewMessage(undefined);
		}
		if (
			scrollContainer.scrollTop < 50 &&
			!previousMessagesLoading &&
			hasMoreMessages
		) {
			fetchPreviousMessages();
		}
	};
	const debouncedOnScroll = debounce(onScroll, 100);

	return (
		<div className={styles['chat-list']}>
			<>
				<div
					ref={chatListMeasurementRef}
					className={styles['chat-list__measurement']}
				/>
				<div
					ref={chatListRef}
					onScroll={debouncedOnScroll}
					className={classNames([
						styles['chat-list__msg-container'],
						padding === 'increased' &&
							styles[
								'chat-list__msg-container--increased-padding'
							],
						isCapacitor && styles['capacitor'],
					])}
					style={{
						height: containerHeight ? containerHeight : '100%',
						overflow: previousMessagesLoading ? 'hidden' : 'auto',
					}}
				>
					<div className={styles['chat-list__msg-container-wrapper']}>
						{previousMessagesLoading && (
							<div className={styles['loading']}>
								<CircularProgress />
							</div>
						)}

						<div className={styles['load-more']}>
							{!hasMoreMessages && (
								<div className={styles['label']}>
									This is the start of the conversation
								</div>
							)}
						</div>

						<div className="vsb-10" />

						{focusedMessageId && (
							<div
								className={styles['overlay']}
								onClick={() => setFocusedMessageId(undefined)}
							/>
						)}

						{(() => {
							const allMessages = placeUserStatsInMessages(
								chat?.messages?.items || [],
								userStats || [],
								user.id,
							);
							const cutoff = subDays(new Date(), 30);
							const splitIdx = isGated
								? allMessages.findIndex((m) => new Date(m.createdAt) >= cutoff)
								: -1;
							const cutoffIdx = splitIdx === -1 ? allMessages.length : splitIdx;
							const oldMessages = isGated && cutoffIdx > 0 ? allMessages.slice(0, cutoffIdx) : [];
							const recentMessages = allMessages.slice(oldMessages.length);

							const renderMsg = (message: typeof allMessages[0], index: number) => {
							const previousMessage =
								index > 0 ? allMessages[index - 1] : null;
							const nextMessage =
								index + 1 < allMessages.length
									? allMessages[index + 1]
									: null;

							const messageCreatedAtDate = new Date(
								message.createdAt,
							);
							const nextMessageCreatedAtDate = new Date(
								nextMessage?.createdAt || '',
							);
							const timeDifferenceFromPreviousMessage = Math.abs(
								differenceInMinutes(
									messageCreatedAtDate,
									new Date(previousMessage?.createdAt || ''),
								),
							);
							const timeDifferenceFromNextMessage = Math.abs(
								differenceInMinutes(
									messageCreatedAtDate,
									nextMessageCreatedAtDate,
								),
							);
							const isPreviousMessageFromDifferentStack =
								timeDifferenceFromPreviousMessage > 15;
							const isNextMessageFromDifferentStack =
								timeDifferenceFromNextMessage > 15;

							const isBot = message.userId === ASAPH_BOT_USER_ID;
							const isAuthor = message.userId === user.id;

							const isPreviousMessageFromSameAuthor =
								previousMessage?.userId === message.userId;
							const isNextMessageFromSameAuthor =
								nextMessage?.userId === message.userId;
							const isFocused = Boolean(
								focusedMessageId &&
									focusedMessageId === message.id,
							);

							const authorFieldContent = isBot
								? ASAPH_BOT_DISPLAY_NAME
								: message.user?.displayName || 'Unknown';

							const position = getStackPosition(
								isPreviousMessageFromSameAuthor &&
									!isPreviousMessageFromDifferentStack,
								isNextMessageFromSameAuthor &&
									!isNextMessageFromDifferentStack,
							);

							const isUserStatsOpen =
								openMessageUserStats === message.id;

							// const isSeenByEveryone = (userStats || []).every(
							// 	(us) => chat?.userIds.includes(us.userId),
							// );
							const seenUserIds = (userStats || [])
								.filter((us) => us.userId !== user.id)
								.filter(
									(us) =>
										new Date(us.lastSeenAt) >
										messageCreatedAtDate,
								)
								.map((us) => us.userId);

							const isSeenByEveryoneButCurrentUser =
								!!chat?.userIds &&
								chat.userIds
									.filter((uid) => uid !== user.id)
									.every((uid) => seenUserIds.includes(uid));

							const seenUserDisplayNames =
								isSeenByEveryoneButCurrentUser &&
								type === 'group'
									? [`everyone`]
									: (seenUserIds
											.map(
												(uid) =>
													findTeamMembersByUserId(uid)
														?.displayName,
											)
											.filter(Boolean) as string[]);

							return (
								<div
									key={`msg-${message.id}`}
									className={classNames([
										styles['message-wrapper'],
										isAuthor
											? styles['sent']
											: styles['received'],
										isUserStatsOpen && styles['active'],
									])}
								>
									{isPreviousMessageFromDifferentStack ||
									!previousMessage ? (
										<div className={styles['sent-at']}>
											{formatMessageSentTime(
												message.createdAt,
											)}
										</div>
									) : null}
									{/* <pre>
												{JSON.stringify({
													isPreviousMessageFromSameAuthor,
													isPreviousMessageFromDifferentStack,
													timeDifferenceFromPreviousMessage,
													isNextMessageFromSameAuthor,
													isNextMessageFromDifferentStack,
													timeDifferenceFromNextMessage,
												})}
                  </pre> */}
									{(() => {
										const showAvatar =
											!isAuthor &&
											!isBot &&
											(!position || position === 'last');
										const showHeader =
											!isAuthor &&
											type !== 'private' &&
											(!isPreviousMessageFromSameAuthor ||
												isPreviousMessageFromDifferentStack);
										const needsAvatarSpacer =
											!isAuthor &&
											!isBot &&
											(position === 'first' ||
												position === 'middle');
										const messageProps = {
											id: message.id,
											type: message.type,
											content: message.content,
											metadata: message.metadata,
											isAuthor,
											isBot,
											setFocusedMessageId,
											isFocused,
											isSending:
												message.createdAt ===
												sendingMessageStatus,
											reactions: message.reactions,
											chatId: chat?.id || '',
											position,
											className: classNames([
												isUserStatsOpen &&
													styles['active'],
											]),
											isUserStatsOpen,
											onClick: (messageId: string) =>
												setOpenMessageUserStats(
													(state) => {
														console.log(messageId);
														// If there is already an open user stats panel
														// then toggle it
														return state ===
															messageId
															? null
															: messageId;
													},
												),
										};
										if (showAvatar) {
											return (
												<div
													className={
														styles['message-row']
													}
												>
													<div
														className={
															styles[
																'message-avatar'
															]
														}
														onClick={() =>
															setSelectedProfileMemberId(
																message.userId,
															)
														}
													>
														<UserAvatar
															userId={
																message.userId
															}
															size="xx-small"
															showActiveStatus={
																false
															}
														/>
													</div>
													<div
														className={
															styles[
																'message-content'
															]
														}
													>
														{showHeader && (
															<>
																<div
																	className={
																		styles[
																			'message-header-tap'
																		]
																	}
																	onClick={() =>
																		setSelectedProfileMemberId(
																			message.userId,
																		)
																	}
																>
																	<MessageHeader
																		content={
																			authorFieldContent
																		}
																	/>
																</div>
																<div className="vsb-5" />
															</>
														)}
														<RenderMessageMemoized
															{...messageProps}
														/>
													</div>
												</div>
											);
										}
										return (
											<>
												{showHeader && (
													<>
														<div
															className={classNames(
																[
																	styles[
																		'message-header-tap'
																	],
																	needsAvatarSpacer &&
																		styles[
																			'message-avatar-spacer'
																		],
																],
															)}
															onClick={() =>
																setSelectedProfileMemberId(
																	message.userId,
																)
															}
														>
															<MessageHeader
																content={
																	authorFieldContent
																}
															/>
														</div>
														<div className="vsb-5" />
													</>
												)}
												<RenderMessageMemoized
													{...messageProps}
													className={classNames([
														messageProps.className,
														needsAvatarSpacer &&
															styles[
																'message-avatar-spacer'
															],
													])}
												/>
											</>
										);
									})()}

									<div
										className={classNames([
											styles['text'],
											isUserStatsOpen &&
												styles['visible'],
										])}
									>
										{seenUserDisplayNames.length
											? `Seen by ${seenUserDisplayNames.join(
													', ',
											  )}`
											: `Sent`}
									</div>

									{!!message.userStats.length && (
										<div className={styles['user-stats']}>
											{message.userStats.map((u) => (
												<UserAvatar
													key={`avatar-${u.userId}`}
													className={styles['stat']}
													userId={u.userId}
													size="xxxx-small"
													showActiveStatus={false}
												/>
											))}
										</div>
									)}

									{!position || position === 'last' ? (
										<div className="vsb-10" />
									) : null}
								</div>
							);
						};

						return (
							<>
								<ChatHistoryGate isGated={!!isGated} isAdmin={!!isAdmin}>
									{oldMessages.map((m, i) => renderMsg(m, i))}
								</ChatHistoryGate>
								{recentMessages.map((m, i) => renderMsg(m, oldMessages.length + i))}
							</>
						);
					})()}
					</div>
				</div>

				{newMessage && (
					<div
						className={styles['new-message']}
						onClick={handleNewMessageClick}
					>
						<div className={styles['tooltip']}>
							New message from {newMessage?.user?.displayName}
						</div>
					</div>
				)}
				<ProfileModal
					memberId={
						selectedProfileMemberId ??
						lastSelectedProfileMemberIdRef.current ??
						''
					}
					open={selectedProfileMemberId !== null}
					onClose={() => setSelectedProfileMemberId(null)}
				/>
			</>
		</div>
	);
};

const formatMessageSentTime = (createdAt: string) => {
	const createdAtDate = new Date(createdAt);
	const nowDate = new Date();
	if (isSameDay(createdAtDate, nowDate)) {
		return format(createdAtDate, 'HH:mm');
	}
	if (isSameWeek(createdAtDate, nowDate)) {
		return format(createdAtDate, 'eee HH:mm');
	}
	if (isSameYear(createdAtDate, nowDate)) {
		return format(createdAtDate, 'dd MMM, HH:mm');
	}
	return format(createdAtDate, 'dd MMM yyyy, HH:mm');
};

const getStackPosition = (
	isPreviousMessageFromSameAuthor: boolean,
	isNextMessageFromSameAuthor: boolean,
): RenderMessageProps['position'] => {
	switch (true) {
		case isPreviousMessageFromSameAuthor && isNextMessageFromSameAuthor:
			return 'middle';
		case isNextMessageFromSameAuthor:
			return 'first';
		case isPreviousMessageFromSameAuthor:
			return 'last';
		default:
			return undefined;
	}
};
type MessageWithUserStat = ChatContainerMessage & {
	userStats: UserChatStat[];
};
const placeUserStatsInMessages = (
	items: ChatContainerMessage[],
	userStats: UserChatStat[],
	userId: string,
): MessageWithUserStat[] => {
	let remainingUsersToPlace = userStats
		.filter((u) => u.userId !== userId)
		.map((us) => us.userId);

	return items.map((message, i, messages) => {
		let messageUserStats: UserChatStat[] = [];
		const nextMessage = messages[i + 1] as ChatContainerMessage | undefined;
		remainingUsersToPlace.map((uid) => {
			const us = userStats.find((us) => us.userId === uid);
			if (!us) {
				return;
			}
			const nextMessageIsNewer = nextMessage
				? us.lastSeenAt < nextMessage.createdAt
				: true;
			if (us.lastSeenAt > message.createdAt && nextMessageIsNewer) {
				messageUserStats.push(us);
				remainingUsersToPlace = remainingUsersToPlace.filter(
					(ruid) => ruid !== us.userId,
				);
			}
		});
		return {
			...message,
			userStats: messageUserStats,
		};
	});
};
